import { useState, useEffect } from 'react';

const NotificationSettings = () => {
  const [formData, setFormData] = useState({
    email: {
      enabled: true,
      notifications: {
        newUser: true,
        newMatch: true,
        matchReminder: true,
        matchCancellation: true,
        paymentConfirmation: true,
        achievementUnlocked: true,
      },
    },
    push: {
      enabled: true,
      notifications: {
        newUser: false,
        newMatch: true,
        matchReminder: true,
        matchCancellation: true,
        paymentConfirmation: false,
        achievementUnlocked: true,
      },
      fcmServerKey: '',
      vapidPublicKey: '',
      vapidPrivateKey: '',
    },
    sms: {
      enabled: false,
      notifications: {
        newUser: false,
        newMatch: false,
        matchReminder: true,
        matchCancellation: true,
        paymentConfirmation: false,
        achievementUnlocked: false,
      },
      provider: 'twilio',
      accountSid: '',
      authToken: '',
      fromNumber: '',
    },
    templates: {
      newUser: {
        subject: 'Bienvenido a ProKidsApp',
        content: 'Hola {{name}}, ¡Bienvenido a ProKidsApp! Tu cuenta ha sido creada correctamente.',
      },
      newMatch: {
        subject: 'Nuevo partido disponible',
        content:
          'Hola {{name}}, hay un nuevo partido disponible: {{match_title}} el {{match_date}} a las {{match_time}}.',
      },
      matchReminder: {
        subject: 'Recordatorio de partido',
        content:
          'Hola {{name}}, te recordamos que tienes un partido mañana: {{match_title}} a las {{match_time}}.',
      },
      matchCancellation: {
        subject: 'Partido cancelado',
        content:
          'Hola {{name}}, lamentamos informarte que el partido {{match_title}} ha sido cancelado.',
      },
      paymentConfirmation: {
        subject: 'Pago confirmado',
        content:
          'Hola {{name}}, tu pago de {{amount}} por el partido {{match_title}} ha sido confirmado.',
      },
      achievementUnlocked: {
        subject: '¡Has desbloqueado un logro!',
        content: 'Hola {{name}}, ¡Felicidades! Has desbloqueado el logro {{achievement_name}}.',
      },
    },
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);

  useEffect(() => {
    // En una implementación real, obtendríamos estos datos de la API
    // Por ahora, usamos los datos de ejemplo definidos en el estado inicial
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: type === 'checkbox' ? checked : value,
          },
        });
      } else if (parts.length === 3) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: type === 'checkbox' ? checked : value,
            },
          },
        });
      } else if (parts.length === 4) {
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: {
                ...formData[parts[0]][parts[1]][parts[2]],
                [parts[3]]: type === 'checkbox' ? checked : value,
              },
            },
          },
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // En una implementación real, enviaríamos estos datos a la API
      console.log('Datos del formulario:', formData);

      // Simulamos una respuesta exitosa
      setTimeout(() => {
        setSuccess(true);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Error al guardar la configuración');
      setLoading(false);
    }
  };

  const handleTemplateEdit = (templateKey) => {
    setActiveTemplate(templateKey);
  };

  const handleTemplateChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      templates: {
        ...formData.templates,
        [activeTemplate]: {
          ...formData.templates[activeTemplate],
          [name]: value,
        },
      },
    });
  };

  const handleCloseTemplateModal = () => {
    setActiveTemplate(null);
  };

  if (loading && Object.keys(formData).length === 0) {
    return <div className="loading">Cargando configuración...</div>;
  }

  return (
    <div className="settings-section">
      <h2>Configuración de Notificaciones</h2>
      <p className="settings-description">
        Configura cómo y cuándo se envían las notificaciones a los usuarios.
      </p>

      {success && <div className="alert alert-success">Configuración guardada correctamente.</div>}

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h3>Canales de Notificación</h3>

          <div className="notification-channel">
            <div className="notification-channel-header">
              <div className="notification-channel-title">
                <h4>Correo Electrónico</h4>
                <span className="material-icons">email</span>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="email.enabled"
                  name="email.enabled"
                  checked={formData.email.enabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="email.enabled">Activar notificaciones por correo</label>
              </div>
            </div>

            {formData.email.enabled && (
              <div className="notification-types">
                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.newUser"
                      name="email.notifications.newUser"
                      checked={formData.email.notifications.newUser}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.newUser">Nuevo usuario</label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('newUser')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>

                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.newMatch"
                      name="email.notifications.newMatch"
                      checked={formData.email.notifications.newMatch}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.newMatch">Nuevo partido</label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('newMatch')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>

                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.matchReminder"
                      name="email.notifications.matchReminder"
                      checked={formData.email.notifications.matchReminder}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.matchReminder">
                      Recordatorio de partido
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('matchReminder')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>

                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.matchCancellation"
                      name="email.notifications.matchCancellation"
                      checked={formData.email.notifications.matchCancellation}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.matchCancellation">
                      Cancelación de partido
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('matchCancellation')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>

                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.paymentConfirmation"
                      name="email.notifications.paymentConfirmation"
                      checked={formData.email.notifications.paymentConfirmation}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.paymentConfirmation">
                      Confirmación de pago
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('paymentConfirmation')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>

                <div className="notification-type">
                  <div className="form-group checkbox">
                    <input
                      type="checkbox"
                      id="email.notifications.achievementUnlocked"
                      name="email.notifications.achievementUnlocked"
                      checked={formData.email.notifications.achievementUnlocked}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email.notifications.achievementUnlocked">
                      Logro desbloqueado
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleTemplateEdit('achievementUnlocked')}
                    title="Editar plantilla"
                  >
                    <span className="material-icons">edit</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="notification-channel">
            <div className="notification-channel-header">
              <div className="notification-channel-title">
                <h4>Notificaciones Push</h4>
                <span className="material-icons">notifications</span>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="push.enabled"
                  name="push.enabled"
                  checked={formData.push.enabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="push.enabled">Activar notificaciones push</label>
              </div>
            </div>

            {formData.push.enabled && (
              <>
                <div className="notification-types">
                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.newUser"
                        name="push.notifications.newUser"
                        checked={formData.push.notifications.newUser}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.newUser">Nuevo usuario</label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.newMatch"
                        name="push.notifications.newMatch"
                        checked={formData.push.notifications.newMatch}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.newMatch">Nuevo partido</label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.matchReminder"
                        name="push.notifications.matchReminder"
                        checked={formData.push.notifications.matchReminder}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.matchReminder">
                        Recordatorio de partido
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.matchCancellation"
                        name="push.notifications.matchCancellation"
                        checked={formData.push.notifications.matchCancellation}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.matchCancellation">
                        Cancelación de partido
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.paymentConfirmation"
                        name="push.notifications.paymentConfirmation"
                        checked={formData.push.notifications.paymentConfirmation}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.paymentConfirmation">
                        Confirmación de pago
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="push.notifications.achievementUnlocked"
                        name="push.notifications.achievementUnlocked"
                        checked={formData.push.notifications.achievementUnlocked}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="push.notifications.achievementUnlocked">
                        Logro desbloqueado
                      </label>
                    </div>
                  </div>
                </div>

                <div className="push-settings">
                  <div className="form-group">
                    <label htmlFor="push.fcmServerKey">Firebase Cloud Messaging Server Key</label>
                    <input
                      type="password"
                      id="push.fcmServerKey"
                      name="push.fcmServerKey"
                      value={formData.push.fcmServerKey}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="••••••••"
                    />
                    <small className="form-text">
                      Dejar en blanco para mantener la clave actual
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="push.vapidPublicKey">VAPID Public Key</label>
                    <input
                      type="text"
                      id="push.vapidPublicKey"
                      name="push.vapidPublicKey"
                      value={formData.push.vapidPublicKey}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Clave pública VAPID"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="push.vapidPrivateKey">VAPID Private Key</label>
                    <input
                      type="password"
                      id="push.vapidPrivateKey"
                      name="push.vapidPrivateKey"
                      value={formData.push.vapidPrivateKey}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="••••••••"
                    />
                    <small className="form-text">
                      Dejar en blanco para mantener la clave actual
                    </small>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="notification-channel">
            <div className="notification-channel-header">
              <div className="notification-channel-title">
                <h4>SMS</h4>
                <span className="material-icons">sms</span>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="sms.enabled"
                  name="sms.enabled"
                  checked={formData.sms.enabled}
                  onChange={handleInputChange}
                />
                <label htmlFor="sms.enabled">Activar notificaciones por SMS</label>
              </div>
            </div>

            {formData.sms.enabled && (
              <>
                <div className="notification-types">
                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.newUser"
                        name="sms.notifications.newUser"
                        checked={formData.sms.notifications.newUser}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.newUser">Nuevo usuario</label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.newMatch"
                        name="sms.notifications.newMatch"
                        checked={formData.sms.notifications.newMatch}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.newMatch">Nuevo partido</label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.matchReminder"
                        name="sms.notifications.matchReminder"
                        checked={formData.sms.notifications.matchReminder}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.matchReminder">
                        Recordatorio de partido
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.matchCancellation"
                        name="sms.notifications.matchCancellation"
                        checked={formData.sms.notifications.matchCancellation}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.matchCancellation">
                        Cancelación de partido
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.paymentConfirmation"
                        name="sms.notifications.paymentConfirmation"
                        checked={formData.sms.notifications.paymentConfirmation}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.paymentConfirmation">
                        Confirmación de pago
                      </label>
                    </div>
                  </div>

                  <div className="notification-type">
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        id="sms.notifications.achievementUnlocked"
                        name="sms.notifications.achievementUnlocked"
                        checked={formData.sms.notifications.achievementUnlocked}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="sms.notifications.achievementUnlocked">
                        Logro desbloqueado
                      </label>
                    </div>
                  </div>
                </div>

                <div className="sms-settings">
                  <div className="form-group">
                    <label htmlFor="sms.provider">Proveedor de SMS</label>
                    <select
                      id="sms.provider"
                      name="sms.provider"
                      value={formData.sms.provider}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="twilio">Twilio</option>
                      <option value="nexmo">Nexmo</option>
                      <option value="aws">AWS SNS</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="sms.accountSid">Account SID</label>
                    <input
                      type="text"
                      id="sms.accountSid"
                      name="sms.accountSid"
                      value={formData.sms.accountSid}
                      onChange={handleInputChange}
                      className="form-control"
                      required={formData.sms.enabled}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="sms.authToken">Auth Token</label>
                    <input
                      type="password"
                      id="sms.authToken"
                      name="sms.authToken"
                      value={formData.sms.authToken}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="••••••••"
                      required={formData.sms.enabled}
                    />
                    <small className="form-text">
                      Dejar en blanco para mantener el token actual
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="sms.fromNumber">Número de origen</label>
                    <input
                      type="text"
                      id="sms.fromNumber"
                      name="sms.fromNumber"
                      value={formData.sms.fromNumber}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="+34600000000"
                      required={formData.sms.enabled}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>

      {activeTemplate && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>
                Editar Plantilla:{' '}
                {activeTemplate === 'newUser'
                  ? 'Nuevo usuario'
                  : activeTemplate === 'newMatch'
                    ? 'Nuevo partido'
                    : activeTemplate === 'matchReminder'
                      ? 'Recordatorio de partido'
                      : activeTemplate === 'matchCancellation'
                        ? 'Cancelación de partido'
                        : activeTemplate === 'paymentConfirmation'
                          ? 'Confirmación de pago'
                          : activeTemplate === 'achievementUnlocked'
                            ? 'Logro desbloqueado'
                            : ''}
              </h2>
              <button className="modal-close" onClick={handleCloseTemplateModal}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label htmlFor="subject">Asunto</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.templates[activeTemplate].subject}
                    onChange={handleTemplateChange}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="content">Contenido</label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.templates[activeTemplate].content}
                    onChange={handleTemplateChange}
                    className="form-control"
                    rows="6"
                    required
                  ></textarea>
                  <small className="form-text">
                    Puedes usar las siguientes variables: &#123;&#123;name&#125;&#125;,
                    &#123;&#123;match_title&#125;&#125;, &#123;&#123;match_date&#125;&#125;,
                    &#123;&#123;match_time&#125;&#125;, &#123;&#123;amount&#125;&#125;,
                    &#123;&#123;achievement_name&#125;&#125;
                  </small>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseTemplateModal}
                  >
                    Cerrar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
